import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import api from '../../../services/api';
import { serializeFilters, toAnalyticsParams } from '../pmAnalyticsFilters';

// PM Analitik P4 — E05 Yetkinlik ve Gelişim Açıkları, E06 Değerlendirme Farkları ve 360, E07 Yönetici Değerlendirme Davranışı'nın
// ORTAK veri katmanı. Uçlar: GET v1/performance-evaluation-analytics/{competency-gaps|review-gaps|rater-behavior}/* (+ liste
// uçlarında export=xlsx). api.js GET'te reject ETMEZ — hata sinyali res.data.status (false/eksik → panel hata durumu; "boş" ayrı).
// GİZLİLİK: yanıtlar 360 değerlendiricisinin kimliğini/tekil puanını taşımaz; gizli hücrede değer null + suppressionReason dolu.

export const BASE = '/v1/performance-evaluation-analytics';
export const COMPETENCY_GAPS_PATH = '/performance/competency-gaps';
export const REVIEW_GAPS_PATH = '/performance/review-gaps';
export const RATER_BEHAVIOR_PATH = '/performance/rater-behavior';
// E14 çalışan profili: /performance/profile/:employeeId?periodId=…&tab=… (P7a sayfasının sözleşmesi; sekmeler ProfileTabs)
export const PROFILE_PATH = '/performance/profile';
export const CALIBRATION_PATH = '/performance/calibration-sessions';
export const ONE_ON_ONES_PATH = '/performance/one-on-ones';
export const SUPPRESSED_REASON = 'ha.suppressed.belowThreshold';

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

export class EvalPanelError extends Error {}

/** Panel isteği: başarısız yanıt → hata (panel "Tekrar Dene" gösterir); veri yoksa bile status true ise boş durumdur. */
export async function evalGet(path, params) {
  const res = await api.get(`${BASE}/${path}`, { params });
  const data = payload(res);
  if (data === undefined) throw new EvalPanelError(res?.data?.message || 'error');
  return data;
}

export const evalExport = (path, params) => api.get(`${BASE}/${path}`, { params: { ...params, export: 'xlsx' }, responseType: 'blob' });

/** Ortak süzgeç (dönem, organizasyon…) + ekran süzgeçleri → uç parametreleri. Boş değerler gönderilmez. */
export function evalParams(filters, screen = {}) {
  const params = { ...toAnalyticsParams(filters) };
  Object.entries(screen).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return;
    params[k] = Array.isArray(v) ? v.join(',') : String(v);
  });
  return params;
}

// Excel indirme: başarısız GET'te interceptor hatayı zaten gösterir ve başlıksız {status:false} döner — sessizce çıkılır.
export function downloadBlob(res, fallbackName) {
  if (!res?.headers || res.data?.status === false) return false;
  const blob = new Blob([res.data], { type: res.headers['content-type'] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
  a.download = match ? match[1].replaceAll('"', '') : fallbackName;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

export const profileLink = (employeeId, periodId, tab) => {
  const p = new URLSearchParams();
  if (periodId) p.set('periodId', periodId);
  if (tab) p.set('tab', tab);
  const s = p.toString();
  return `${PROFILE_PATH}/${employeeId}${s ? `?${s}` : ''}`;
};

/** Başka bir P4 ekranına ortak süzgeçleri taşıyarak geçiş (ör. E07 yönetici satırı → E06 o yöneticinin ekibi). */
export const withFilters = (path, filters, extra = {}) => {
  const base = serializeFilters(filters);
  const p = new URLSearchParams(base.startsWith('?') ? base.slice(1) : base);
  Object.entries(extra).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') p.set(k, String(v)); });
  const s = p.toString();
  return `${path}${s ? `?${s}` : ''}`;
};

/**
 * Ekran düzeyi URL durumu (ortak süzgeç anahtarlarıyla çakışmayan kısa anahtarlar): yenileme ve geri dönüşte korunur. `set`
 * verilen anahtarları yazar (null/''/[] → siler); diğer anahtarlara (ortak süzgeç dahil) dokunmaz. Çoklu değer virgülle.
 * İkinci argüman (`nextFilters`, ortak süzgecin TAM yeni hali) verilirse aynı URL güncellemesinde yazılır — iki ayrı replace
 * aynı eski konumdan hesaplanıp birbirini ezmesin (ör. ısı haritası hücresi: birim süzgeci + yetkinlik süzgeci birlikte).
 */
export function useScreenParams(keys) {
  const location = useLocation();
  const history = useHistory();
  const keyStr = keys.join(',');
  const values = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return Object.fromEntries(keyStr.split(',').map((k) => [k, p.get(k)]));
  }, [location.search, keyStr]);
  const set = useCallback((patch, nextFilters) => {
    const p = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([k, v]) => {
      const empty = v === null || v === undefined || v === '' || v === false || (Array.isArray(v) && v.length === 0);
      if (empty) p.delete(k);
      else p.set(k, Array.isArray(v) ? v.join(',') : String(v));
    });
    const s = p.toString();
    let search = s ? `?${s}` : '';
    if (nextFilters) search = serializeFilters(nextFilters, search);
    history.replace({ pathname: location.pathname, search });
  }, [history, location.pathname, location.search]);
  return [values, set];
}

export const listOf = (v) => (v ? String(v).split(',').map((x) => x.trim()).filter(Boolean) : []);

// ---------------------------------------------------------------- değer yardımcıları
export const isSuppressed = (x) => !!x?.suppressionReason;
export const metricByCode = (list, code) => (list || []).find((m) => m.metricCode === code) || null;
export const formatNum = (v, lang, digits = 2) => (v === null || v === undefined ? '' : new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(Number(v)));
export const formatSigned = (v, lang, digits = 2) => (v === null || v === undefined ? '' : `${Number(v) > 0 ? '+' : ''}${formatNum(v, lang, digits)}`);

/** Metrik metni: gizli → "Gizli", değer yok (payda 0) → "Veri Yok"; yüzdede "%" sonek, puanda "/ ölçek". */
export function metricText(t, ns, m, lang) {
  if (!m) return t('performance.operations.kpi.noData');
  if (isSuppressed(m)) return t(`${ns}.suppressed`);
  if (m.value === null || m.value === undefined) return t('performance.operations.kpi.noData');
  if (m.unit === 'percent') return `${formatNum(m.value, lang)}%`;
  if (m.unit === 'score' && m.scaleMax && Number(m.value) >= 0) return `${formatNum(m.value, lang)} / ${formatNum(m.scaleMax, lang, 0)}`;
  return formatNum(m.value, lang);
}

/** "n = 12 · Kapsama %80" alt satırı (gizli metrikte yalnız gizlilik notu). */
export function metricSub(t, ns, m, lang) {
  if (!m) return '';
  if (isSuppressed(m)) return t(`${ns}.suppressedNote`);
  const parts = [];
  if (m.sampleN !== null && m.sampleN !== undefined) parts.push(t(`${ns}.sampleN`, { n: formatNum(m.sampleN, lang, 0) }));
  if (m.coverage !== null && m.coverage !== undefined) parts.push(t(`${ns}.coverageValue`, { value: formatNum(m.coverage, lang) }));
  return parts.join(' · ');
}

/** Panel meta (şeritteki "Son güncelleme"): canlı hesaplanan yanıtın bağlamından. */
export const metaOfContext = (data) => ({ calculatedAt: data?.context?.calculatedAt || null, stale: false });

/** Sapma rengi (işaretli fark): negatif tehlike, pozitif ikincil, yakın nötr. Tema token'ı döner. */
export function gapTone(theme, gap) {
  if (gap === null || gap === undefined) return theme.colors.text.muted;
  if (Number(gap) < 0) return theme.colors.auxiliar.danger;
  if (Number(gap) > 0) return theme.colors.secondary.dark;
  return theme.colors.text.primary;
}

/** Tema renginden (hex) saydam ton — ısı haritası hücresi. Ham renk yazılmaz; yalnız token'dan türetilir. */
export function tint(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${Math.max(0, Math.min(1, alpha))})`;
}
